import React, { useState, useEffect } from "react";
import { MdEmail } from "react-icons/md";
import axios from "axios";
import toastr from "toastr";
import { CustomLoading } from "components/CustomLoading";
import FixedEmployeeCampaignTable from "enroll/policy/Fixed_employee_campaign";
import NewFixedCampaignModal from "components/New_fixed_campaign";
import { Line } from "ktm-ui";
import EditFixedPlanCampaignModal from "components/Edit_fixed_plan_campaign";

interface FixedPlanCampaignProps {
    policy_id: string;
}

const FixedPlanCampaign: React.FC<FixedPlanCampaignProps> = ({ policy_id }) => {
    const [loading, setLoading] = useState(true);
    const [hasCampaign, setHasCampaign] = useState(false);
    const [benefitEndDate, setBenefitEndDate] = useState<string | null>(null);
    const [nypUnlockDate, setNypUnlockDate] = useState<string | null>(null);
    const [nypLocked, setNypLocked] = useState(true);
    const [hasAccrualEnabled, setHasAccrualEnabled] = useState(false);
    const [nextYearAccrualFrequency, setNextYearAccrualFrequency] =
        useState<string | null>(null);
    const [showNewFixedCampaignModal, setShowNewFixedCampaignModal] = useState(false);
    const [reopenNewModal, setReopenNewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [reopenEditModal, setReopenEditModal] = useState(false);
    const [isStartingCampaign, setIsStartingCampaign] = useState(false);
    const [isSavingCampaign, setIsSavingCampaign] = useState(false);

    useEffect(() => {
        loadData();
    }, [policy_id]);

    const loadData = async () => {
        setLoading(true);
        await Promise.all([getCampaignInfo(), getBenefitEndDate()]);
        setLoading(false);
    };

    const getCampaignInfo = async () => {
        try {
            const [campaignRes, creditRes] = await Promise.all([
                axios.get(`/api/FixedCampaign/getpolicyfixedcampaign`, {
                    params: { PolicyId: policy_id }
                }),
                axios.get(`/api/Utility/GetScheduledCreditInfo`, {
                    params: { policyId: policy_id }
                })
            ]);

            setHasCampaign(campaignRes.data?.success === true);

            if (creditRes.data?.success === true) {
                setHasAccrualEnabled(creditRes.data.hasAccrualEnabled === true);
                setNextYearAccrualFrequency(
                    creditRes.data.nextYearAccrualFrequency ?? null
                );
            } else {
                setHasAccrualEnabled(false);
                setNextYearAccrualFrequency(null);
            }
        } catch {
            toastr.error("Error getting campaign info", "Error");
            setHasCampaign(false);
            setHasAccrualEnabled(false);
            setNextYearAccrualFrequency(null);
        }
    };

    const getBenefitEndDate = async () => {
        try {
            const response = await axios.get(`/api/Flex/getbenefitenddate`, {
                params: { policyId: policy_id }
            });

            if (response.data?.success) {
                setBenefitEndDate(response.data.currentBenYearEndDate.endDate);
                setNypUnlockDate(response.data.nypUnlockDate ?? null);
                setNypLocked(response.data.nypLocked !== false);
            } else {
                toastr.error(response.data?.message || "Failed to load benefit end date");
            }
        } catch {
            toastr.error("Failed to get benefit end date", "Error");
        }
    };

    const hasAccrual =
        hasAccrualEnabled && !!nextYearAccrualFrequency;

    if (loading || isStartingCampaign || isSavingCampaign) {
        return <CustomLoading />;
    }

    const renderCampaignDetails = () => (
        <div className="campaign-info-container">
            <EditFixedPlanCampaignModal
                showEditCampaignModal={showEditModal || reopenEditModal}
                closeModal={() => {
                    setShowEditModal(false);
                    setReopenEditModal(false);
                }}
                refreshCampaigns={getCampaignInfo}
                policy_id={policy_id}
                nextYearAccrualFrequency={nextYearAccrualFrequency}
                setParentLoading={setIsSavingCampaign}
                onFailedSave={() => setReopenEditModal(true)}
            />

            {hasAccrual && (
                <div className="RT-Section">
                    <div className="RT-Section__header">
                        <div
                            className="RT-Section__title"
                            style={{ fontSize: 16, fontWeight: "bold" }}
                        >
                            New Year Plan Details
                        </div>
                        <div className="RT-Section__actions">
                            <a
                                href="#"
                                className="RT-Link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowEditModal(true);
                                }}
                            >
                                Edit
                            </a>
                        </div>
                    </div>

                    <Line>
                        <div className="RT-Static-Field">
                            <label className="RT-Static-Field__label">
                                Scheduled Credits Frequency
                            </label>
                            <div className="RT-Static-Field__content">
                                {nextYearAccrualFrequency}
                            </div>
                        </div>
                    </Line>
                </div>
            )}

            <div className="employee-campaigns-container" style={{ marginTop: 15 }}>
                <FixedEmployeeCampaignTable
                    policy_id={policy_id}
                    benefitEndDate={benefitEndDate}
                    HasAccrual={hasAccrual}
                />
            </div>
        </div>
    );

    const renderEmptyState = () => (
        <div
            className="campaign-empty-state"
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 60,
                textAlign: "center"
            }}
        >
            <MdEmail style={{ fill: "rgb(100,120,140)", fontSize: 80, opacity: 0.2 }} />

            <button
                className="RT-Button RT-Button--primary"
                style={{
                    width: 250,
                    height: 40,
                    fontSize: 17,
                    opacity: nypLocked ? 0.5 : 1,
                    cursor: nypLocked ? "not-allowed" : "pointer"
                }}
                onClick={() => setShowNewFixedCampaignModal(true)}
                disabled={nypLocked}
            >
                Start New Year Plan
            </button>

            {nypLocked && nypUnlockDate && (
                <div style={{ marginTop: 10, color: "rgb(100,120,140)", fontSize: 14 }}>
                    Available on {nypUnlockDate} (within 1 month of the current
                    benefit year end)
                </div>
            )}

            <NewFixedCampaignModal
                policy_id={policy_id}
                showNewFixedCampaignModal={showNewFixedCampaignModal || reopenNewModal}
                closeModal={() => {
                    setShowNewFixedCampaignModal(false);
                    setReopenNewModal(false);
                }}
                refreshCampaigns={getCampaignInfo}
                setParentLoading={setIsStartingCampaign}
                onFailedStart={() => setReopenNewModal(true)}
                accrualFrequency={
                    hasAccrualEnabled ? nextYearAccrualFrequency : null
                }
            />
        </div>
    );

    return hasCampaign ? renderCampaignDetails() : renderEmptyState();
};

export default FixedPlanCampaign;
